import { API_BASE_URL } from '../../constants/appConstants';
import { authenticatedFilesRequest, authenticatedRequest } from '../authService';

const SUPERADMIN_BASE_URL = `${API_BASE_URL}/superadmin`;

/**
 * Download a document attachment
 * @param {number} documentId - Document ID to download
 * @returns {Promise<Blob>} File blob for download
 */
export const downloadAttachment = async (documentId) => {
  if (!documentId) {
    throw new Error('Document ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/documents/${documentId}/download/`;

  try {
    // Use authenticatedFilesRequest for proper token handling with file downloads
    const response = await authenticatedFilesRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'Failed to download attachment';
      
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the status text
        errorMessage = response.statusText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `document_${documentId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    
    return {
      success: true,
      blob,
      filename
    };
  } catch (error) {
    console.error('Error downloading attachment:', error);
    throw new Error(error.message || 'Failed to download attachment');
  }
};

/**
 * Get document information without downloading
 * @param {number} documentId - Document ID
 * @returns {Promise<Object>} Document information
 */
export const getDocumentInfo = async (documentId) => {
  if (!documentId) {
    throw new Error('Document ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/documents/${documentId}/info/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch document info');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching document info:', error);
    throw new Error(error.message || 'Failed to fetch document info');
  }
};

/**
 * Download an expense attachment
 * @param {number} attachmentId - Expense attachment ID to download
 * @returns {Promise<Object>} File blob and filename for download
 */
export const downloadExpenseAttachment = async (attachmentId) => {
  if (!attachmentId) {
    throw new Error('Expense attachment ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/attachments/expenses/${attachmentId}/download/`;

  try {
    const response = await authenticatedFilesRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'Failed to download expense attachment';
      
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `expense_attachment_${attachmentId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    
    return {
      success: true,
      blob,
      filename
    };
  } catch (error) {
    console.error('Error downloading expense attachment:', error);
    throw new Error(error.message || 'Failed to download expense attachment');
  }
};

/**
 * Download a revenue attachment
 * @param {number} attachmentId - Revenue attachment ID to download
 * @returns {Promise<Object>} File blob and filename for download
 */
export const downloadRevenueAttachment = async (attachmentId) => {
  if (!attachmentId) {
    throw new Error('Revenue attachment ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/attachments/revenues/${attachmentId}/download/`;

  try {
    const response = await authenticatedFilesRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'Failed to download revenue attachment';
      
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `revenue_attachment_${attachmentId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    
    return {
      success: true,
      blob,
      filename
    };
  } catch (error) {
    console.error('Error downloading revenue attachment:', error);
    throw new Error(error.message || 'Failed to download revenue attachment');
  }
};

/**
 * Download a bill attachment
 * @param {number} attachmentId - Bill attachment ID to download
 * @returns {Promise<Object>} File blob and filename for download
 */
export const downloadBillAttachment = async (attachmentId) => {
  if (!attachmentId) {
    throw new Error('Bill attachment ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/attachments/bills/${attachmentId}/download/`;

  try {
    const response = await authenticatedFilesRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'Failed to download bill attachment';
      
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `bill_attachment_${attachmentId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    
    return {
      success: true,
      blob,
      filename
    };
  } catch (error) {
    console.error('Error downloading bill attachment:', error);
    throw new Error(error.message || 'Failed to download bill attachment');
  }
};

/**
 * Download a proforma attachment
 * @param {number} attachmentId - Proforma attachment ID to download
 * @returns {Promise<Object>} File blob and filename for download
 */
export const downloadProformaAttachment = async (attachmentId) => {
  if (!attachmentId) {
    throw new Error('Proforma attachment ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/attachments/proformas/${attachmentId}/download/`;

  try {
    const response = await authenticatedFilesRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      let errorMessage = 'Failed to download proforma attachment';
      
      try {
        const errorData = await response.json();
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        errorMessage = response.statusText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `proforma_attachment_${attachmentId}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    
    return {
      success: true,
      blob,
      filename
    };
  } catch (error) {
    console.error('Error downloading proforma attachment:', error);
    throw new Error(error.message || 'Failed to download proforma attachment');
  }
};

/**
 * Helper function to trigger file download in browser
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename for download
 */
export const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};